var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
import ___default from "lodash";
import moment__default from "moment";
import { StudyTypes, StageTrafficSource, ExperimentStepType, TreatmentVariableType, FlightType } from "@ane/exp-data/lib/models";
import { compareIgnoreCase } from "@ane/core/lib/utils/CommonStringUtils";
import { CommonUtilsInstance } from "@ane/core/lib/utils/CommonUtils";
import { getAggregatedState } from "@ane/exp-data/lib/utils/ExperimentStateUtils";
import { parseTrafficFilters } from "@ane/exp-data/lib/utils/FeatureUtils";
import { MasterDataUtilInstance } from "@ane/exp-data/lib/utils/MasterDataUtils";
import { DEFAULT_FEATURE_EXPOSURE_DURATION, DEFAULT_EXPERIMENT_NAME } from "./experiment.constants.js";
class ExperimentUtils {
  parseExperimentEditRules(editRules, isFeatureRollout = false) {
    const parsedPolicyRules = {};
    ___default.map(Object.keys(editRules.Policies), (policyKey) => {
      const policyRules = editRules.Policies[policyKey];
      parsedPolicyRules[policyKey] = {};
      ___default.map(Object.keys(policyRules), (fieldKey) => {
        parsedPolicyRules[policyKey][fieldKey] = {
          readonly: !!policyRules[fieldKey].readonly,
          // @ts-ignore
          required: policyRules[fieldKey].required == null ? false : policyRules[fieldKey].required.params,
          // @ts-ignore
          requiredMessage: policyRules[fieldKey].required == null ? "" : policyRules[fieldKey].required.message
        };
      });
    });
    const minExposureDuration = !!isFeatureRollout ? DEFAULT_FEATURE_EXPOSURE_DURATION : editRules.ExposureDuration.minDuration;
    return {
      name: this.parsePropertyRules(editRules.Name, "Experiment Name is required"),
      description: this.parsePropertyRules(editRules.Description, "Experiment Description is required"),
      owners: { required: true, readonly: false, requiredMessage: "Experiment owners must be specified" },
      policies: parsedPolicyRules,
      exposureDuration: __spreadProps(__spreadValues({}, this.parsePropertyRules(editRules.ExposureDuration, "Exposure Duration is required")), {
        min: moment__default.duration(minExposureDuration).asDays(),
        max: moment__default.duration(editRules.ExposureDuration.maxDuration).asDays()
      }),
      trafficFilters: this.parsePropertyRules(editRules.TrafficFilters, ""),
      isHidden: this.parsePropertyRules(editRules.IsHidden, ""),
      flights: editRules.Flights ? this.parseFlightEditRules(editRules.Flights) : { flightRules: {}, readonly: false }
    };
  }
  parseFlightEditRules(flightRules) {
    const result = {};
    ___default.map(flightRules.elements, (flightRule) => {
      if (flightRule.MappingKey && (!!flightRule.MappingKey.Name || flightRule.MappingKey.Name === "")) {
        const trafficExposureRules = {};
        if (!!flightRule.TrafficExposureSequence) {
          ___default.map(Object.keys(flightRule.TrafficExposureSequence), (phaseName) => {
            const rule = flightRule.TrafficExposureSequence[phaseName];
            trafficExposureRules[phaseName] = __spreadProps(__spreadValues({}, this.parsePropertyRules(rule, "")), { min: rule.min, max: rule.max });
          });
        }
        result[flightRule.MappingKey.Name] = {
          name: this.parsePropertyRules(flightRule.Name, "Flight name is required"),
          description: this.parsePropertyRules(flightRule.Description, "Flight name is required"),
          flightType: this.parsePropertyRules(flightRule.FlightType, "Flight name is required"),
          isolationGroup: this.parsePropertyRules(flightRule.IsolationGroup, ""),
          trafficExposureSequence: trafficExposureRules
        };
      }
    });
    return { flightRules: result, readonly: flightRules.readonly };
  }
  getExperimentTemplate(experimentTemplates, templateName = "") {
    if (!experimentTemplates)
      return null;
    return Object.values(experimentTemplates).find((template) => !!template && compareIgnoreCase(template.Name, templateName));
  }
  getTemplateName(experimentTemplate) {
    let templateTitle = DEFAULT_EXPERIMENT_NAME;
    const { Workflow, IsFeatureRollout, StudyType, Title = "" } = experimentTemplate || {};
    if (!!Workflow && !IsFeatureRollout && StudyType !== StudyTypes.FeatureRollout && StudyType !== StudyTypes.RolloutTarget) {
      templateTitle = !!Title ? Title : DEFAULT_EXPERIMENT_NAME;
    }
    return templateTitle;
  }
  parseExperimentV2Dto(experiment, mdsFilters, experimentTemplates) {
    const aggregatedState = getAggregatedState(experiment.Stages.map((s) => s.State));
    const trafficFilters = parseTrafficFilters(this.getLastTrafficFilterSet(experiment), mdsFilters, true);
    const allTrafficFilters = MasterDataUtilInstance.getTrafficFiltersFromMDSFilters(mdsFilters, true);
    const { TemplateName: templateName = "" } = experiment;
    const experimentTemplate = this.getExperimentTemplate(experimentTemplates, templateName);
    const templateTitle = this.getTemplateName(experimentTemplate);
    const experimentStages = this.parseExperimentV2Stages(experiment.Stages, experiment);
    const primaryStages = ___default.filter(experimentStages, (stage) => this.isPrimaryStage(stage));
    const plannedExposureDuration = Math.floor(___default.sum(___default.map(primaryStages, (stage) => stage.plannedExposureDuration)));
    const elapsedExposureDuration = Math.floor(___default.sum(___default.map(primaryStages, (stage) => stage.elapsedExposureDuration)));
    return {
      version: experiment.Version,
      templateName: templateTitle,
      // use Template Title provided by MDS
      template: templateName,
      // @ts-ignore
      mdsTemplate: experimentTemplate,
      created: experiment.Created,
      completed: experiment.Completed,
      // @ts-ignore
      conclusion: this.parseExperimentConclusion(experiment.Conclusion),
      isHidden: experiment.IsHidden,
      isExperimentInitialized: experiment.IsExperimentInitialized,
      isFeatureRollout: experiment.IsFeatureRollout,
      id: experiment.Id,
      // @ts-ignore
      friendlyName: experiment.Name,
      state: aggregatedState,
      // @ts-ignore
      description: experiment.Description,
      // @ts-ignore
      assignmentUnit: experiment.AssignmentUnit,
      // @ts-ignore
      workspaceId: experiment.WorkspaceId,
      // @ts-ignore
      managementGroup: experiment.ManagementGroup,
      // @ts-ignore
      owners: experiment.Owners,
      // @ts-ignore
      clonedFromId: experiment.ClonedFromId,
      // @ts-ignore
      featureRolloutMetadata: this.parseFeatureRolloutMetadata(experiment.FeatureRolloutMetadata),
      mdsTrafficFilters: allTrafficFilters,
      trafficFilters,
      // @ts-ignore
      flights: this.parseExperimentV2Flights(experiment.Flights, experimentStages),
      stages: experimentStages,
      // @ts-ignore
      policies: this.parseExperimentPolicies(experiment.Policies),
      extensions: experiment.Extensions,
      // @ts-ignore
      experimentTrialConfiguration: this.parseExperimentTrialConfiguration(experiment.ExperimentTrialConfiguration),
      plannedExposureDuration,
      elapsedExposureDuration,
      // @ts-ignore
      numberlines: experiment.Numberlines
    };
  }
  parseExperimentV2Stages(stages, experiment) {
    if (!stages || !stages.length) {
      return [];
    }
    const stageResults = ___default.map(stages, (stage) => {
      const steps = this.parseExperimentV2Steps(stage.ParallelSteps);
      return {
        name: "",
        // set below
        displayName: "",
        // set below
        trafficLabel: "",
        // set below
        startTime: !!stage.StartTime ? new Date(stage.StartTime) : null,
        stopTime: !!stage.StopTime ? new Date(stage.StopTime) : null,
        state: stage.State,
        trafficSource: !!stage.TrafficSource && stage.TrafficSource.toLowerCase() === "userspecified" ? StageTrafficSource.userSpecified : StageTrafficSource.templateDefined,
        gatingEntityId: stage.GatingEntityId,
        elapsedExposureDuration: moment__default.duration(stage.ElapsedExposureDuration).asDays(),
        plannedExposureDuration: moment__default.duration(stage.PlannedExposureDuration).asDays(),
        parallelSteps: steps,
        isRampUpStage: stage.IsRampUpStage || false
      };
    });
    return this.getStagesWithPhaseContext(stageResults, experiment);
  }
  /**
   * Generates stage name, stage display name, and stage traffic labels based off the
   * stage context.
   * This logic has been copied over from Exp API:
   * https://experimentation.visualstudio.com/Analysis%20and%20Experimentation/_git/Analysis%20and%20Experimentation?path=%2Fprivate%2FExpMan%2FExpMan%2FExpMan.DomainLogic%2FExperimentRun%2FExperimentDesign.cs&version=GBmaster&line=62&lineStyle=plain&lineEnd=158&lineStartColumn=12&lineEndColumn=14
   * The labels were originally returned as part of the V1 Experiment GET API but are no longer available in the V2 API.
   */
  getStagesWithPhaseContext(stages, experiment) {
    const isRampupExperiment = ___default.some(stages, (stage) => stage.isRampUpStage);
    const isReverseExperiment = (experiment == null ? void 0 : experiment.Policies) && "ReverseExperiment" in experiment.Policies && experiment.Policies["ReverseExperiment"].IsEnabled;
    let preExposure = true;
    const phaseNameCountMapping = {};
    const results = [];
    ___default.forEach(stages, (stage, index) => {
      const stepTypes = ___default.map(stage.parallelSteps, (step) => step.type);
      let name = null;
      let displayName = null;
      let trafficLabel = null;
      if (CommonUtilsInstance.IsArrayEqualIgnoreOrder(stepTypes, [ExperimentStepType.AA])) {
        name = preExposure ? "Pre-AA" : "Post-AA";
        displayName = name;
      } else if (CommonUtilsInstance.IsArrayEqualIgnoreOrder(stepTypes, [ExperimentStepType.SeedFinder]) || CommonUtilsInstance.IsArrayEqualIgnoreOrder(stepTypes, [ExperimentStepType.SeedFinder, ExperimentStepType.AB])) {
        name = "SeedFinder";
        displayName = "Optimize Randomization";
        if (stage.isRampUpStage) {
          const filterSetCount = new Set(___default.map(stage.parallelSteps, (step) => step.trafficFilterSetId)).size;
          if (filterSetCount > 1) {
            name += "FilterRampUp";
            displayName += " & Ramp-up (Filter & Percent)";
          } else {
            name += "RampUp";
            displayName += " & Ramp-up";
          }
          trafficLabel = "Initial Traffic";
        }
      } else if (CommonUtilsInstance.IsArrayEqualIgnoreOrder(stepTypes, [ExperimentStepType.AB]) || CommonUtilsInstance.IsArrayEqualIgnoreOrder(stepTypes, [ExperimentStepType.DataCollection]) || CommonUtilsInstance.IsArrayEqualIgnoreOrder(stepTypes, [ExperimentStepType.Interleaving]) || CommonUtilsInstance.IsArrayEqualIgnoreOrder(stepTypes, [ExperimentStepType.ExternalAB])) {
        name = this.getExposurePhaseName(stage);
        displayName = this.getExposureDisplayName(stage, isReverseExperiment);
        if (stage.isRampUpStage) {
          trafficLabel = "Initial Traffic";
        } else {
          trafficLabel = isRampupExperiment ? "Final Traffic" : "Traffic";
          preExposure = false;
        }
      } else if (CommonUtilsInstance.IsArrayEqualIgnoreOrder(stepTypes, [ExperimentStepType.AA, ExperimentStepType.AB])) {
        if (stage.isRampUpStage) {
          name = "RampUp";
          displayName = "Ramp Up";
          trafficLabel = "Initial Traffic";
        } else {
          name = "ABWithAAStep";
          displayName = "AB with AA step";
          trafficLabel = isRampupExperiment ? "Final Traffic" : "Traffic";
          preExposure = false;
        }
      }
      if (name in phaseNameCountMapping) {
        phaseNameCountMapping[name] += 1;
        name += `-${phaseNameCountMapping[name]}`;
      } else {
        phaseNameCountMapping[name] = 0;
      }
      results.push(__spreadProps(__spreadValues({}, stage), { name, displayName, trafficLabel }));
    });
    return results;
  }
  // Primary stage logic taken from ExP API:
  // https://experimentation.visualstudio.com/Analysis%20and%20Experimentation/_git/Analysis%20and%20Experimentation?path=%2Fprivate%2FExpMan%2FExpMan%2FExpMan.DomainLogic%2FExperimentRun%2FExperimentContext.cs&version=GBmaster&line=607&lineEnd=613&lineStartColumn=1&lineEndColumn=10&lineStyle=plain
  isPrimaryStage(stage) {
    const stepTypes = ___default.map(stage.parallelSteps, (step) => step.type);
    return !stage.isRampUpStage && stage.parallelSteps.length > 0 && stepTypes.includes(ExperimentStepType.AB) || stepTypes.includes(ExperimentStepType.Interleaving) || stepTypes.includes(ExperimentStepType.InterleavingOnly) || stepTypes.includes(ExperimentStepType.DataCollection);
  }
  getExposurePhaseName(exposureStage) {
    const prefix = exposureStage.isRampUpStage ? "Ramp-" : "";
    return prefix + exposureStage.parallelSteps[0].type.toString();
  }
  getExposureDisplayName(exposureStage, isReverse) {
    const prefix = exposureStage.isRampUpStage ? "Ramp Up" : "Run ";
    const postfix = isReverse ? " (reverse)" : "";
    return prefix + exposureStage.parallelSteps[0].type.toString() + postfix;
  }
  parseExperimentV2Steps(steps) {
    return ___default.map(steps, (step) => {
      return {
        id: step.Id,
        // @ts-ignore
        type: this.parseStepType(step.Type),
        trafficFilterSetId: step.TrafficFilterSetId,
        flightTrafficMap: step.FlightTrafficMap,
        flightAllocationIdsMap: step.FlightAllocationIdsMap
      };
    });
  }
  parseStepType(type) {
    if (compareIgnoreCase(type, ExperimentStepType.AA)) {
      return ExperimentStepType.AA;
    } else if (compareIgnoreCase(type, ExperimentStepType.AB)) {
      return ExperimentStepType.AB;
    } else if (compareIgnoreCase(type, ExperimentStepType.DataCollection)) {
      return ExperimentStepType.DataCollection;
    } else if (compareIgnoreCase(type, ExperimentStepType.SeedFinder)) {
      return ExperimentStepType.SeedFinder;
    } else if (compareIgnoreCase(type, ExperimentStepType.Interleaving)) {
      return ExperimentStepType.Interleaving;
    } else if (compareIgnoreCase(type, ExperimentStepType.InterleavingOnly)) {
      return ExperimentStepType.InterleavingOnly;
    } else if (compareIgnoreCase(type, ExperimentStepType.ExternalAB)) {
      return ExperimentStepType.ExternalAB;
    }
    return ExperimentStepType.Unknown;
  }
  parseExperimentPolicies(policies) {
    const result = {};
    ___default.forEach(Object.keys(policies), (policyKey) => {
      result[policyKey] = {
        // @ts-ignore
        description: policies[policyKey].Description,
        // @ts-ignore
        displayName: policies[policyKey].DisplayName,
        // @ts-ignore
        informationUrl: policies[policyKey].InformationUrl,
        // @ts-ignore
        isEnabled: policies[policyKey].IsEnabled,
        // @ts-ignore
        isEnabledByDefault: policies[policyKey].IsEnabledByDefault,
        // @ts-ignore
        name: policies[policyKey].Name,
        // @ts-ignore
        userComment: policies[policyKey].UserComment,
        // @ts-ignore
        userCommentPrompt: policies[policyKey].UserCommentPrompt
      };
    });
    return result;
  }
  parseExperimentTrialConfiguration(trialConfiguration) {
    if (!trialConfiguration) {
      return null;
    }
    return {
      // @ts-ignore
      templateId: trialConfiguration.TemplateId,
      // @ts-ignore
      name: trialConfiguration.Name,
      // @ts-ignore
      sequenceIndex: trialConfiguration.SequenceIndex,
      // @ts-ignore
      mdsSequenceIndex: trialConfiguration.MdsSequenceIndex
    };
  }
  parseExperimentV2Flights(flights, stages) {
    const result = {};
    Object.keys(flights).forEach((flight) => {
      const trafficMap = {};
      ___default.forEach(stages, (stage) => {
        trafficMap[stage.name] = 0;
        ___default.forEach(stage.parallelSteps, (step) => {
          trafficMap[stage.name] += step.flightTrafficMap[flight] || 0;
        });
      });
      result[flight] = {
        name: flights[flight].Name,
        // @ts-ignore
        description: flights[flight].Description,
        // @ts-ignore
        flightType: this.parseFlightType(flights[flight].FlightType),
        // @ts-ignore
        isolationGroups: flights[flight].IsolationGroups,
        // @ts-ignore
        treatmentVariables: ___default.map(flights[flight].TreatmentVariables, (tv) => {
          var _a, _b;
          return {
            name: tv.Key,
            type: ((_a = tv.Type) == null ? void 0 : _a.toLowerCase()) === "boolean" ? TreatmentVariableType.Boolean : ((_b = tv.Type) == null ? void 0 : _b.toLowerCase()) === "int" ? TreatmentVariableType.Number : TreatmentVariableType.String,
            value: tv.Value
          };
        }),
        flightTraffic: trafficMap
      };
    });
    return result;
  }
  parseFlightType(type) {
    if (compareIgnoreCase(type, "treatment")) {
      return FlightType.Treatment;
    } else if (compareIgnoreCase(type, "customcontrol")) {
      return FlightType.CustomControl;
    } else if (compareIgnoreCase(type, "reservetreatment")) {
      return FlightType.ReserveTreatment;
    } else if (compareIgnoreCase(type, "reservecontrol")) {
      return FlightType.ReserveControl;
    } else if (compareIgnoreCase(type, "standardcontrol")) {
      return FlightType.StandardControl;
    } else if (compareIgnoreCase(type, "interleaving")) {
      return FlightType.Interleaving;
    } else {
      return FlightType.CustomControl;
    }
  }
  parseFeatureRolloutMetadata(metadata) {
    if (!metadata)
      return null;
    return {
      // @ts-ignore
      rootExperimentId: metadata.RootExperimentId,
      // @ts-ignore
      name: metadata.Name,
      // @ts-ignore
      description: metadata.Description
    };
  }
  parseExperimentConclusion(conclusion) {
    if (!conclusion)
      return null;
    const { Comment: comment, IsUseful: isUseful, OperationReason: operationReason, User: user } = conclusion;
    return {
      // @ts-ignore
      comment,
      // @ts-ignore
      isUseful,
      // @ts-ignore
      operationReason,
      // @ts-ignore
      user
    };
  }
  getLastTrafficFilterSet(experiment) {
    if (experiment.Stages.length === 0) {
      return {};
    }
    let stagesLength = experiment.Stages.length;
    let lastStageTrafficFilterName = experiment.Stages[stagesLength - 1].ParallelSteps[0].TrafficFilterSetId;
    if (!lastStageTrafficFilterName) {
      return {};
    }
    return experiment.TrafficFilterSets[lastStageTrafficFilterName];
  }
  parsePropertyRules(prop, defaultRequiredMessage) {
    if (!prop) {
      return {
        readonly: false,
        required: false,
        requiredMessage: defaultRequiredMessage
      };
    }
    return {
      readonly: !!prop.readonly,
      required: !prop.required ? false : prop.required.params,
      requiredMessage: !prop.required ? defaultRequiredMessage : prop.required.message
    };
  }
}
const ExperimentUtilsInstance = new ExperimentUtils();
export {
  ExperimentUtils,
  ExperimentUtilsInstance
};
//# sourceMappingURL=ExperimentUtils.js.map
